import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchWithLocalCache } from "../lib/cache";

const STOCK_SCREENER_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/stock_screener.json";

/* -------------------- mobile helper -------------------- */
function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

const columns = [
  { label: "", key: "logo", width: 38 },
  { label: "Ticker", key: "ticker", width: 82 },
  { label: "Rank", key: "rank", width: 64 },
  { label: "Company", key: "name", width: 210 },
  { label: "Allocation", key: "allocation", width: 150, align: "right" },
  { label: "Cost Basis", key: "costBasis", width: 102, align: "right" },
  { label: "Current", key: "currentPrice", width: 102, align: "right" },
  { label: "Return", key: "returnPct", width: 96, align: "right" },
  { label: "Description", key: "description", width: 250 },
];


const mobileColumns = columns
  .filter((col) => ["ticker", "allocation", "currentPrice", "returnPct"].includes(col.key))
  .map((col) => {
    if (col.key === "ticker") {
      return {
        ...col,
        sticky: false,
        left: undefined,
        width: 78,
      };
    }

    return {
      ...col,
      sticky: false,
      left: undefined,
      width:
        col.key === "allocation" ? 112 :
        col.key === "currentPrice" ? 86 :
        col.key === "returnPct" ? 84 :
        col.width,
    };
  });

// Map internal keys -> EXACT sheet header names
const keyMap = {
  rank: ["Rank"],
  name: ["Name", "Company", "Company Name"],
  ticker: ["Ticker", "Symbol"],
  allocation: ["Allocation", "Weight", "Portfolio Weight"],
  description: ["Description", "Desc", "Thesis", "Notes"],
  costBasis: ["Cost Basis", "CostBasis", "Cost", "Average Cost", "Avg Cost", "Avg Cost Basis", "Purchase Price"],
};

function formatPercent(v, decimals = 2) {
  const num = parseNumber(v);
  if (!Number.isFinite(num)) return v === null || v === undefined ? "" : String(v);
  const pct = Math.abs(num) <= 1 ? num * 100 : num;
  return pct.toFixed(decimals) + "%";
}

function formatMoney(v) {
  const n = parseNumber(v);
  if (!Number.isFinite(n)) return v === null || v === undefined || v === "" ? "—" : String(v);
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function getReturnStyle(v) {
  if (!Number.isFinite(v)) return { color: "#64748b" };
  if (v > 0) return { color: "#15803d", background: "#ecfdf5", border: "1px solid #bbf7d0" };
  if (v < 0) return { color: "#b91c1c", background: "#fff1f2", border: "1px solid #fecdd3" };
  return { color: "#475569", background: "#f8fafc", border: "1px solid #e2e8f0" };
}

function parseNumber(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const cleaned = s.replace(/[$,%]/g, "").replace(/,/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function normalizeHeaderKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/[^\w ]/g, "");
}

function getAnyField(row, candidates) {
  if (!row || typeof row !== "object") return undefined;

  const lookup = new Map();
  for (const key of Object.keys(row)) {
    lookup.set(normalizeHeaderKey(key), key);
  }

  for (const candidate of candidates) {
    const realKey = lookup.get(normalizeHeaderKey(candidate));
    if (realKey !== undefined) {
      return row[realKey];
    }
  }

  return undefined;
}


function allocationToPct(v) {
  const n = parseNumber(v);
  if (!Number.isFinite(n)) return null;
  return Math.abs(n) <= 1 ? n * 100 : n;
}

// function formatLargeNumber(n) {
//   if (!Number.isFinite(n)) return "—";
//   return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
// }

function getInitials(nameOrTicker) {
  const s = String(nameOrTicker || "").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function RankBadge({ rank }) {
  const n = parseNumber(rank);
  const isTopTen = Number.isFinite(n) && n <= 10;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 34,
        height: 26,
        padding: "0 9px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 900,
        background: isTopTen ? "#eff6ff" : "#f8fafc",
        color: isTopTen ? "#1d4ed8" : "#475569",
        border: isTopTen ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
      }}
    >
      {rank || "—"}
    </span>
  );
}

function AllocationBar({ value, maxValue, compact = false }) {
  const pct = allocationToPct(value);
  const safeMax = Number.isFinite(maxValue) && maxValue > 0 ? maxValue : 1;
  const width = Number.isFinite(pct) ? Math.max(2, Math.min(100, (pct / safeMax) * 100)) : 0;

  return (
    <div style={{ display: "grid", gap: 6, minWidth: compact ? 92 : 120 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "baseline",
          gap: 6,
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 900, color: "#0f172a" }}>
          {Number.isFinite(pct) ? `${pct.toFixed(2)}%` : "—"}
        </span>
      </div>
      <div
        style={{
          height: 6,
          width: "100%",
          background: "#e2e8f0",
          borderRadius: 999,
          overflow: "hidden",
          boxShadow: "inset 0 1px 2px rgba(15,23,42,0.08)",
        }}
      >
        <div
          style={{
            width: `${width}%`,
            height: "100%",
            borderRadius: 999,
            background: "linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)",
          }}
        />
      </div>
    </div>
  );
}

function CompanyLogo({ ticker, name }) {
  const [failed, setFailed] = useState(false);
  const cleanTicker = String(ticker || "").trim().toUpperCase();
  const logoUrl = cleanTicker
    ? `https://financialmodelingprep.com/image-stock/${encodeURIComponent(cleanTicker)}.png`
    : "";

  if (!logoUrl || failed) {
    return (
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #e0f2fe 0%, #eef2ff 100%)",
          color: "#1e40af",
          fontSize: 11,
          fontWeight: 900,
          border: "1px solid #dbeafe",
        }}
      >
        {getInitials(cleanTicker || name)}
      </div>
    );
  }

  return (
    <img
      src={logoUrl}
      alt=""
      width={24}
      height={24}
      style={{
        borderRadius: 8,
        display: "block",
        objectFit: "contain",
        background: "#fff",
        border: "1px solid #e2e8f0",
        padding: 4,
        boxShadow: "0 4px 10px rgba(15,23,42,0.08)",
      }}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

// function MetricCard({ label, value, sub }) {
//   return (
//     <div
//       style={{
//         padding: 16,
//         borderRadius: 18,
//         background: "rgba(255,255,255,0.1)",
//         border: "1px solid rgba(255,255,255,0.16)",
//         boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
//       }}
//     >
//       <div
//         style={{
//           fontSize: 11,
//           letterSpacing: "0.08em",
//           textTransform: "uppercase",
//           color: "#cbd5e1",
//           fontWeight: 800,
//           marginBottom: 8,
//         }}
//       >
//         {label}
//       </div>
//       <div style={{ fontSize: 24, fontWeight: 950, letterSpacing: "-0.04em" }}>
//         {value}
//       </div>
//       {sub ? <div style={{ marginTop: 4, fontSize: 12, color: "#94a3b8" }}>{sub}</div> : null}
//     </div>
//   );
// }


function renderHoldingCell({
  col,
  ticker,
  name,
  rank,
  allocationRaw,
  description,
  costBasis,
  currentPrice,
  returnPct,
  maxAllocation,
}) {
  if (col.key === "logo") {
    return <CompanyLogo ticker={ticker} name={name} />;
  }

  if (col.key === "ticker") {
    return ticker ? (
      <Link
        to={`/stocks2/${encodeURIComponent(ticker)}`}
        style={{
          textDecoration: "none",
          color: "#2563eb",
          fontWeight: 950,
          letterSpacing: "0.02em",
        }}
      >
        {ticker}
      </Link>
    ) : (
      "—"
    );
  }

  if (col.key === "rank") {
    return <RankBadge rank={rank} />;
  }

  if (col.key === "name") {
    return name || "—";
  }

  if (col.key === "allocation") {
    return <AllocationBar value={allocationRaw} maxValue={maxAllocation} compact={col.width <= 112} />;
  }

  if (col.key === "costBasis") {
    return Number.isFinite(costBasis) ? formatMoney(costBasis) : "—";
  }

  if (col.key === "currentPrice") {
    return Number.isFinite(currentPrice) ? formatMoney(currentPrice) : "—";
  }

  if (col.key === "returnPct") {
    return (
      <span
        style={{
          display: "inline-flex",
          justifyContent: "center",
          minWidth: 72,
          padding: "5px 8px",
          borderRadius: 999,
          fontWeight: 900,
          ...getReturnStyle(returnPct),
        }}
      >
        {Number.isFinite(returnPct) ? formatPercent(returnPct, 2) : "—"}
      </span>
    );
  }

  if (col.key === "description") {
    return description || "—";
  }

  return "—";
}

export default function Holdings() {
  const isMobile = useIsMobile(900);

  const [items, setItems] = useState([]);
  const [currentPrices, setCurrentPrices] = useState({});
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [sortKey, setSortKey] = useState("allocation");
  const [sortDir, setSortDir] = useState("desc");

  const TH_PAD = isMobile ? "7px 5px" : "8px 8px";
  const TD_PAD = isMobile ? "7px 5px" : "8px 8px";
  const HEADER_FONT = isMobile ? 8 : 10;
  const CELL_FONT = isMobile ? 10 : 11;

  const LOGO_W = isMobile ? 38 : 42;
  const TICKER_W = isMobile ? 78 : 84;

  const visibleColumns = isMobile ? mobileColumns : columns;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setError("");
        setLoading(true);

        const base = process.env.REACT_APP_SHEETS_API_URL;
        if (!base) throw new Error("Missing REACT_APP_SHEETS_API_URL (.env + restart)");

        const apiKey = process.env.REACT_APP_API_KEY;
        if (!apiKey) throw new Error("Missing REACT_APP_API_KEY (.env + restart)");

        const url = `${base}?type=holdings&view=list&key=${encodeURIComponent(apiKey)}`;

        const res = await fetchWithLocalCache(url, "holdings_list_v4", 60 * 60 * 1000);
        const payload = res?.data ?? res;

        if (!cancelled) setItems(Array.isArray(payload) ? payload : []);
      } catch (e) {
        if (!cancelled) setError(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadCurrentPrices() {
      try {
        const res = await fetch(STOCK_SCREENER_URL, { cache: "no-store" });
        const text = await res.text();

        if (!res.ok || text.trim().startsWith("<")) return;

        const json = JSON.parse(text);
        const data = Array.isArray(json?.data) ? json.data : [];

        const priceMap = {};
        for (const row of data) {
          const ticker = String(row?.ticker || "").trim().toUpperCase();
          const price = parseNumber(row?.price);
          if (ticker && Number.isFinite(price)) {
            priceMap[ticker] = price;
          }
        }

        if (!cancelled) setCurrentPrices(priceMap);
      } catch (err) {
        console.error("Unable to load current prices", err);
      }
    }

    loadCurrentPrices();

    return () => {
      cancelled = true;
    };
  }, []);

  function getCell(row, colKey) {
    const candidates = keyMap[colKey];
    if (!candidates) return undefined;
    return getAnyField(row, Array.isArray(candidates) ? candidates : [candidates]);
  }

  const enrichedItems = useMemo(() => {
    return items.map((row, index) => {
      const ticker = String(getCell(row, "ticker") ?? "").trim().toUpperCase();
      const name = String(getCell(row, "name") ?? "").trim();
      const allocationPct = allocationToPct(getCell(row, "allocation"));
      const rankNum = parseNumber(getCell(row, "rank"));
      const description = String(getCell(row, "description") ?? "").trim();
      const costBasis = parseNumber(getCell(row, "costBasis"));
      const currentPrice = currentPrices[ticker] ?? null;
      const returnPct =
        Number.isFinite(costBasis) &&
        costBasis !== 0 &&
        Number.isFinite(currentPrice)
          ? (currentPrice - costBasis) / costBasis
          : null;

      return {
        row,
        index,
        ticker,
        name,
        description,
        allocationPct,
        rankNum,
        costBasis,
        currentPrice,
        returnPct,
      };
    });
  }, [items, currentPrices]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return enrichedItems;

    return enrichedItems.filter((item) => {
      const haystack = [
        item.ticker,
        item.name,
        getCell(item.row, "rank"),
        getCell(item.row, "allocation"),
        item.description,
        item.costBasis,
        item.currentPrice,
        item.returnPct,
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [enrichedItems, query]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;

    return [...filtered].sort((a, b) => {
      if (sortKey === "allocation") {
        const av = a.allocationPct;
        const bv = b.allocationPct;
        if (Number.isFinite(av) && !Number.isFinite(bv)) return -1;
        if (!Number.isFinite(av) && Number.isFinite(bv)) return 1;
        if (Number.isFinite(av) && Number.isFinite(bv)) return (av - bv) * dir;
      }

      if (sortKey === "rank") {
        const av = a.rankNum;
        const bv = b.rankNum;
        if (Number.isFinite(av) && !Number.isFinite(bv)) return -1;
        if (!Number.isFinite(av) && Number.isFinite(bv)) return 1;
        if (Number.isFinite(av) && Number.isFinite(bv)) return (av - bv) * dir;
      }

      if (sortKey === "costBasis" || sortKey === "currentPrice" || sortKey === "returnPct") {
        const av = a[sortKey];
        const bv = b[sortKey];
        if (Number.isFinite(av) && !Number.isFinite(bv)) return -1;
        if (!Number.isFinite(av) && Number.isFinite(bv)) return 1;
        if (Number.isFinite(av) && Number.isFinite(bv)) return (av - bv) * dir;
      }

      const av = String(sortKey === "description" ? a.description : getCell(a.row, sortKey) ?? "").trim().toLowerCase();
      const bv = String(sortKey === "description" ? b.description : getCell(b.row, sortKey) ?? "").trim().toLowerCase();

      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  const stats = useMemo(() => {
    const allocations = enrichedItems
      .map((x) => x.allocationPct)
      .filter((x) => Number.isFinite(x));

    const totalAllocation = allocations.reduce((sum, x) => sum + x, 0);
    const maxAllocation = allocations.length ? Math.max(...allocations) : 0;
    const topHolding = enrichedItems
      .filter((x) => Number.isFinite(x.allocationPct))
      .sort((a, b) => b.allocationPct - a.allocationPct)[0];
    const topFiveTotal = enrichedItems
      .filter((x) => Number.isFinite(x.allocationPct))
      .sort((a, b) => b.allocationPct - a.allocationPct)
      .slice(0, 5)
      .reduce((sum, x) => sum + x.allocationPct, 0);

    return {
      totalAllocation,
      maxAllocation,
      topHolding,
      topFiveTotal,
    };
  }, [enrichedItems]);

  function onHeaderClick(colKey) {
    if (colKey === "logo") return;

    if (sortKey === colKey) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(colKey);
      if (colKey === "allocation") setSortDir("desc");
      else setSortDir("asc");
    }
  }

  const stickyLeftBase = {
    position: "sticky",
    zIndex: 4,
    background: "#fff",
  };

  const stickyLogo = {
    ...stickyLeftBase,
    left: 0,
    width: LOGO_W,
    minWidth: LOGO_W,
    maxWidth: LOGO_W,
    boxShadow: "1px 0 0 rgba(226,232,240,0.9)",
  };

  const stickyTicker = {
    ...stickyLeftBase,
    left: LOGO_W,
    width: TICKER_W,
    minWidth: TICKER_W,
    maxWidth: TICKER_W,
    boxShadow: "2px 0 0 rgba(226,232,240,0.9)",
  };

  const stickyHeaderBase = {
    position: "sticky",
    top: 0,
    zIndex: 7,
    background: "#f8fafc",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
        padding: isMobile ? 12 : 24,
        fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
        color: "#0f172a",
      }}
    >
      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
        <section
          style={{
            borderRadius: 26,
            padding: isMobile ? 14 : 18,
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #334155 100%)",
            color: "white",
            boxShadow: "0 24px 60px rgba(15,23,42,0.22)",
            marginBottom: 18,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              right: -80,
              top: -120,
              width: 280,
              height: 280,
              borderRadius: "50%",
              background: "rgba(59,130,246,0.22)",
              filter: "blur(4px)",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: -110,
              bottom: -150,
              width: 320,
              height: 320,
              borderRadius: "50%",
              background: "rgba(124,58,237,0.18)",
              filter: "blur(4px)",
            }}
          />

          <div style={{ position: "relative", zIndex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 900,
                    color: "#93c5fd",
                    letterSpacing: "0.13em",
                    textTransform: "uppercase",
                    marginBottom: 8,
                  }}
                >
                  Portfolio Holdings
                </div>
                <h1
                  style={{
                    margin: 0,
                    fontSize: isMobile ? 24 : 32,
                    lineHeight: 1,
                    letterSpacing: "-0.055em",
                  }}
                >
                  Allan Porter Holdings
                </h1>
                <p
                  style={{
                    margin: "10px 0 0",
                    maxWidth: 720,
                    color: "#cbd5e1",
                    fontSize: 14,
                    lineHeight: 1.6,
                  }}
                >
                </p>
              </div>
            </div>
          </div>
        </section>

        <section
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 22,
            background: "rgba(255,255,255,0.86)",
            boxShadow: "0 16px 40px rgba(15,23,42,0.1)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: isMobile ? 14 : 18,
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              gap: 12,
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            }}
          >
            <div>
              <div style={{ fontSize: 18, fontWeight: 900, letterSpacing: "-0.03em" }}>
                Holdings Table
              </div>
              <div style={{ color: "#64748b", fontSize: 13, marginTop: 3 }}>
                {isMobile
                  ? ""
                  : "Current Allan Porter Primary holdings"}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div
                style={{
                  position: "relative",
                  width: isMobile ? "100%" : 360,
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    left: 13,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#94a3b8",
                    fontSize: 14,
                  }}
                >
                  ⌕
                </span>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search ticker, company, rank..."
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "12px 14px 12px 34px",
                    minWidth: 0,
                    fontSize: 14,
                    border: "1px solid #cbd5e1",
                    borderRadius: 14,
                    outline: "none",
                    background: "white",
                    boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
                  }}
                />
              </div>

              <div
                style={{
                  color: "#334155",
                  fontSize: 13,
                  background: "#f1f5f9",
                  border: "1px solid #e2e8f0",
                  borderRadius: 999,
                  padding: "9px 12px",
                  whiteSpace: "nowrap",
                }}
              >
                Showing <strong>{sorted.length}</strong> / <strong>{items.length}</strong>
              </div>
            </div>
          </div>

          {loading && <div style={{ padding: 18, color: "#64748b" }}>Loading holdings…</div>}
          {error && (
            <div
              style={{
                margin: 16,
                padding: 14,
                color: "#9f1239",
                background: "#fff1f2",
                border: "1px solid #fecdd3",
                borderRadius: 14,
              }}
            >
              Error: {error}
            </div>
          )}

          {!loading && !error && (
            <div
              style={{
                overflowX: isMobile ? "hidden" : "auto",
                overflowY: "auto",
                maxHeight: "72vh",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <table
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: isMobile ? 0 : 1180,
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  tableLayout: "fixed",
                }}
              >
                <thead>
                  <tr>
                    {visibleColumns.map((c) => {
                      const isLogo = c.key === "logo";
                      const isTicker = c.key === "ticker";
                      const isActive = sortKey === c.key;

                      return (
                        <th
                          key={c.key}
                          onClick={() => onHeaderClick(c.key)}
                          style={{
                            padding: TH_PAD,
                            fontSize: HEADER_FONT,
                            fontWeight: 900,
                            color: isActive ? "#1d4ed8" : "#64748b",
                            cursor: c.key === "logo" ? "default" : "pointer",
                            userSelect: "none",
                            borderBottom: "1px solid #e2e8f0",
                            textAlign: c.align || "left",
                            whiteSpace: "nowrap",
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                            width: isMobile ? undefined : c.width,
                            minWidth: isMobile ? 0 : c.width,
                            maxWidth: isMobile ? undefined : c.width,
                            ...(stickyHeaderBase || {}),
                            ...(isLogo && c.sticky ? { ...stickyLogo, ...stickyHeaderBase, zIndex: 11 } : {}),
                            ...(isTicker && c.sticky ? { ...stickyTicker, ...stickyHeaderBase, zIndex: 11 } : {}),
                          }}
                          title={c.key === "logo" ? "" : "Click to sort"}
                        >
                          {c.label}
                          {isActive ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {sorted.length === 0 ? (
                    <tr>
                      <td colSpan={visibleColumns.length} style={{ padding: 24, color: "#64748b", textAlign: "center" }}>
                        No holdings match your search.
                      </td>
                    </tr>
                  ) : (
                    sorted.map((item, displayIdx) => {
                      const row = item.row;
                      const ticker = item.ticker;
                      const rank = String(getCell(row, "rank") ?? "").trim();
                      const name = item.name;
                      const allocationRaw = getCell(row, "allocation");
                      const description = item.description;
                      const costBasis = item.costBasis;
                      const currentPrice = item.currentPrice;
                      const returnPct = item.returnPct;
                      const rowBg = displayIdx % 2 === 0 ? "#ffffff" : "#fbfdff";

                      return (
                        <tr key={`${ticker || "row"}-${item.index}`} style={{ background: rowBg }}>
                          {visibleColumns.map((col) => {
                            const isLogo = col.key === "logo";
                            const isTicker = col.key === "ticker";

                            return (
                              <td
                                key={col.key}
                                style={{
                                  padding: TD_PAD,
                                  fontSize: CELL_FONT,
                                  borderBottom: "1px solid #eef2f7",
                                  whiteSpace:
                                    col.key === "name" || col.key === "description"
                                      ? "normal"
                                      : "nowrap",
                                  overflowWrap:
                                    col.key === "name" || col.key === "description"
                                      ? "break-word"
                                      : undefined,
                                  lineHeight:
                                    col.key === "name" || col.key === "description"
                                      ? 1.2
                                      : undefined,
                                  textAlign: col.align || "left",
                                  fontVariantNumeric:
                                    col.align === "right" ? "tabular-nums" : undefined,
                                  width: isMobile ? undefined : col.width,
                                  minWidth: isMobile ? 0 : col.width,
                                  maxWidth: isMobile ? undefined : col.width,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  ...(isLogo && col.sticky ? { ...stickyLogo, background: rowBg } : {}),
                                  ...(isTicker && col.sticky ? { ...stickyTicker, background: rowBg } : {}),
                                }}
                              >
                                {renderHoldingCell({
                                  col,
                                  ticker,
                                  name,
                                  rank,
                                  allocationRaw,
                                  description,
                                  costBasis,
                                  currentPrice,
                                  returnPct,
                                  maxAllocation: stats.maxAllocation,
                                })}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
